/**
 * Internal Accountant WorkDesk — Phase 3.1
 *
 * Brings AccountantDashboard (Queue/My-Cases/Signed-Off tabs) and its
 * 8 sidebar widgets into the app shell as the accountant_internal WorkDesk.
 *
 * Design: Black+Orange (near-black surfaces, #FF6600 brand signal only,
 * #22C55E done-green for signed-off states).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useUser } from '../../../contexts/TestAuthContext';
import { api } from '../../../utils/api';
import { BRAND_NAME } from '../../../config/brand';
import {
  FileText,
  CheckCircle,
  AlertTriangle,
  Unlock,
  X,
  LayoutGrid,
  ExternalLink,
  ClipboardCheck,
  Download,
  ChevronDown,
  TrendingUp,
  History,
  Search,
} from 'lucide-react';

// Import tab components (will be created inline for self-containment)
import SignOffReadinessCard from '../../../components/accountant/SignOffReadinessCard';
import HistoryModal from '../../../components/accountant/HistoryModal';
import NowLineBanner from '../../../components/accountant/NowLineBanner';
import QueueTriageRail from '../../../components/accountant/QueueTriageRail';
import CaseTable from '../../../components/accountant/CaseTable';
import PaginationBar from '../../../components/accountant/PaginationBar';
import HandoffBlock from '../../../components/handoff/HandoffBlock';
import DataQualityCard from '../../../components/accountant/DataQualityCard';
import WarRoomSummaryCard from '../../../components/accountant/WarRoomSummaryCard';
import ForensicInsightsCard from '../../../components/dashboard/ForensicInsightsCard';
import InsuranceQuestionsCard from '../../../components/dashboard/InsuranceQuestionsCard';
import NextActionsChecklist from '../../../components/dashboard/NextActionsChecklist';
import IndustryBenchmarkCard from '../../../components/dashboard/IndustryBenchmarkCard';
import AIActivityCard from '../../../components/dashboard/AIActivityCard';
import { FinancialValue } from '../../../components/shared/FinancialValue';
// Phase 4 (D4 amendments #2/#3) — reused unmodified, self-fetching rail
// panels (docs/plans/2026-08-22-accountant-tier1-4-plan.md §3.10). Mounted
// only when their new accordion section is open (mount-on-open, matching
// the five existing Case Context sections below).
import CaseReadinessPanel from '../../../components/CaseReadinessPanel';
import ExtractedDocumentsPanel from '../../../components/ExtractedDocumentsPanel';
// Phase 5 (D5 shell/nav) — chromeless-family utility strip (refresh/
// settings/identity/logout) + self-contained pipeline toasts. Neither
// depends on AppLayout; both are mounted directly at the WorkDesk root.
import WorkDeskUtilityBar from '../../../components/workdesk/WorkDeskUtilityBar';
import AgentNotification from '../../../components/pipeline/AgentNotification';
// Phase 2 (D1 toolbar) — local, WorkDesk-only filter hook (K3 Part-1 ruling:
// do NOT extend the shared useCaseFilter; see the hook file's header comment).
import { useWorkdeskCaseFilter, type WorkdeskStatusFilterValue } from './useWorkdeskCaseFilter';
// Canonical case shape (Phase 0 type unification, 2026-08-22 —
// docs/plans/2026-08-22-accountant-tier1-4-plan.md §1.3 step 4). Was a
// local, thinner interface here; now imports the same type CaseTable and
// the rest of the accountant surfaces use, so `data.cases` (already shaped
// like AccountantCaseResponse, queue_api.py:866-938) needs no `as any` cast.
import type { CaseSummary } from '../../../components/accountant/types';

interface QueueStats {
  total_cases: number;
  needs_attention: number;
  no_action_needed: number;
  accountant: number;
  ready: number;
  total_value: number;
}

interface NowLineCategory { label: string; count: number }
interface NowLineSide { count?: number; breakdown?: NowLineCategory[] }
interface NowLineStats { needs_attention?: NowLineSide; no_action_needed?: NowLineSide }

type TabKey = 'queue' | 'signed';

const TAB_KEYS: TabKey[] = ['queue', 'signed'];
const DEFAULT_TAB: TabKey = 'queue';

const TAB_ALIASES: Record<string, TabKey> = {
  'signed-off': 'signed',
  signed_off: 'signed',
  signedoff: 'signed',
  // Claim-pool removed — legacy ?tab=mine deep links land on the unified list.
  mine: 'queue',
};

function parseTab(value: string | null): TabKey {
  if (!value) return DEFAULT_TAB;
  if (TAB_KEYS.includes(value as TabKey)) {
    return value as TabKey;
  }
  const alias = TAB_ALIASES[value.toLowerCase()];
  if (alias) return alias;
  return DEFAULT_TAB;
}

// Phase 2 (D1 toolbar) — the WorkDesk's `?filter=` param, extended from its
// prior partial handling (only 'needs-review'/'ready', stat-card-driven) to
// the full four-value set the toolbar dropdown offers. Unknown/absent values
// fall back to 'all' (pass-through), same fallback shape as parseTab above.
const STATUS_FILTER_VALUES: WorkdeskStatusFilterValue[] = ['ready', 'needs-review', 'stale'];
function parseStatusFilter(value: string | null): WorkdeskStatusFilterValue {
  if (value && (STATUS_FILTER_VALUES as string[]).includes(value)) {
    return value as WorkdeskStatusFilterValue;
  }
  return 'all';
}

// Server page size — matches the hardcoded `page_size=50` in loadQueue/
// loadQueue below (queue_api.py's default). Used only for the pager's
// rangeStart/rangeEnd honesty-cluster math.
const PAGE_SIZE = 50;
function pageRange(page: number, totalCount: number): { start: number; end: number } {
  if (totalCount <= 0) return { start: 0, end: 0 };
  const start = (page - 1) * PAGE_SIZE + 1;
  const end = Math.min(page * PAGE_SIZE, totalCount);
  return { start, end };
}

// Simple Queue Tab Content (presentational — parent owns the data)
function QueueCasesList({
  cases,
  loading,
  selectedCaseId,
  onSelectCase,
  onSignOff,
  isFiltered,
  onClearFilter,
}: {
  cases: CaseSummary[];
  loading: boolean;
  selectedCaseId: string | null;
  onSelectCase: (id: string) => void;
  /** Row SIGN OFF button handler (opens the rail + locus; does not POST). */
  onSignOff: (id: string) => void;
  /** True when a search query or non-'all' status filter is active — Phase 2 (D1). */
  isFiltered: boolean;
  onClearFilter: () => void;
}) {
  if (loading) {
    return (
      <div className="p-8 text-center text-text-muted">
        Loading queue...
      </div>
    );
  }

  if (cases.length === 0) {
    // Phase 2 (D1): a filter/search narrowing the queue to zero rows is a
    // different message than the queue genuinely having none — the former
    // renders inside the table (header stays visible) with a way back out.
    if (isFiltered) {
      return (
        <CaseTable
          cases={cases}
          variant="queue"
          selectedCaseId={selectedCaseId}
          onSelectCase={onSelectCase}
          onSignOff={onSignOff}
          filterEmpty
          onClearFilter={onClearFilter}
        />
      );
    }
    return (
      <div className="p-8 text-center">
        <div className="w-12 h-12 bg-bg-elevated rounded-full flex items-center justify-center mx-auto mb-3">
          <Unlock className="w-6 h-6 text-text-muted" />
        </div>
        <p className="text-text-muted">No cases available in queue</p>
      </div>
    );
  }

  return (
    <CaseTable
      cases={cases}
      variant="queue"
      selectedCaseId={selectedCaseId}
      onSelectCase={onSelectCase}
      onSignOff={onSignOff}
    />
  );
}

// Simple Signed Off List (presentational — parent passes the already-filtered signed-off list)
function SignedOffList({
  cases,
  loading,
  selectedCaseId,
  onSelectCase,
}: {
  cases: CaseSummary[];
  loading: boolean;
  selectedCaseId: string | null;
  onSelectCase: (id: string) => void;
}) {
  if (loading) {
    return (
      <div className="p-8 text-center text-text-muted">
        Loading signed off cases...
      </div>
    );
  }

  if (cases.length === 0) {
    return (
      <div className="p-8 text-center">
        <div className="w-12 h-12 bg-done-bg rounded-full flex items-center justify-center mx-auto mb-3">
          <CheckCircle className="w-6 h-6 text-done" />
        </div>
        <p className="text-text-muted">No signed off cases yet</p>
      </div>
    );
  }

  return (
    <CaseTable cases={cases} variant="signed" selectedCaseId={selectedCaseId} onSelectCase={onSelectCase} />
  );
}

/**
 * WorkdeskToolbar — Phase 2 (D1). Renders directly above CaseTable on the
 * Queue and My Cases tabs ("the toolbar must be visually coupled to the
 * thing it filters" — pixel-pass verdict). Search (left) + status dropdown
 * (right, `?filter=`-bound). Orange appears in exactly two places here: the
 * search input's focus ring, and the 6×6px tick beside the dropdown when a
 * filter is active — both location/state signals, never a resting fill.
 *
 * Ported/restyled from the search + status-filter markup at TabShell.tsx
 * ~:97-133 (TabShell itself is left untouched — old tabs keep using it).
 */
function WorkdeskToolbar({
  searchQuery,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
}: {
  searchQuery: string;
  onSearchChange: (v: string) => void;
  statusFilter: WorkdeskStatusFilterValue;
  onStatusFilterChange: (v: WorkdeskStatusFilterValue) => void;
}) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
      <div className="relative flex-1 max-w-[22rem]">
        <Search
          className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none text-text-dim"
          aria-hidden="true"
        />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search name / client / firm / event…"
          aria-label="Search cases by name, client, law firm, or event type"
          data-testid="workdesk-toolbar-search"
          className="w-full h-8 pl-8 pr-7 text-sm rounded-[2px] border border-border text-text-primary placeholder:text-text-dim focus:outline-none focus:border-brand transition-colors duration-150"
          style={{ backgroundColor: 'var(--color-bg-surface)' }}
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => onSearchChange('')}
            aria-label="Clear search"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-text-dim hover:text-text-primary transition-colors duration-150"
          >
            <X className="w-3 h-3" aria-hidden="true" />
          </button>
        )}
      </div>

      <div className="ml-auto flex items-center gap-1.5">
        {statusFilter !== 'all' && (
          <span aria-hidden="true" className="w-1.5 h-1.5 rounded-[2px] bg-brand shrink-0" title="Filter active" />
        )}
        <div className="relative">
          <select
            value={statusFilter}
            onChange={(e) => onStatusFilterChange(e.target.value as WorkdeskStatusFilterValue)}
            aria-label="Filter by case status"
            data-testid="workdesk-toolbar-status"
            className="h-8 min-w-[9.5rem] pl-2.5 pr-7 text-xs rounded-[2px] border border-border text-text-secondary appearance-none focus:outline-none focus:border-brand transition-colors duration-150"
            style={{ backgroundColor: 'var(--color-bg-surface)' }}
          >
            <option value="all">All cases</option>
            <option value="ready">Ready</option>
            <option value="needs-review">Needs review</option>
            <option value="stale">Stale 5+ days</option>
          </select>
          <ChevronDown
            className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-text-dim"
            aria-hidden="true"
          />
        </div>
      </div>
    </div>
  );
}

export default function InternalAccountantWorkDesk() {
  const { user } = useUser();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab: TabKey = parseTab(searchParams.get('tab'));

  const currentAccountantName =
    (user as any)?.fullName || (user as any)?.primaryEmailAddress?.emailAddress || 'Accountant';

  const setActiveTab = useCallback(
    (tab: TabKey) => {
      const next = new URLSearchParams(searchParams);
      next.set('tab', tab);
      setSearchParams(next, { replace: false });
    },
    [searchParams, setSearchParams]
  );

  const [stats, setStats] = useState<QueueStats | null>(null);
  const [queueCases, setQueueCases] = useState<CaseSummary[]>([]);
  const [queueLoading, setQueueLoading] = useState(true);
  // Pagination data layer (§1.2 steps 1-3, 5-6) — per-tab page state + server
  // totals. Placement/styling of the pager is provisional (bottom of card);
  // Phase 2 (D1) finalizes it inside the toolbar.
  const [queuePage, setQueuePage] = useState(1);
  // Scope: 'queue' = my queue, 'all' = every org case at any stage
  const [scope, setScope] = useState<'queue' | 'all'>('queue');
  const [queueTotalPages, setQueueTotalPages] = useState(1);
  const [queueTotalCount, setQueueTotalCount] = useState(0);
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
  const [caseDetails, setCaseDetails] = useState<any | null>(null);
  const [caseDetailsLoading, setCaseDetailsLoading] = useState(false);
  const [caseDetailsError, setCaseDetailsError] = useState<string | null>(null);
  const [accountantPayload, setAccountantPayload] = useState<any | null>(null);
  const [accountantPayloadLoading, setAccountantPayloadLoading] = useState(false);
  // Phase 4 (D4 #3) — bumped by the Industry Benchmarks empty-row's retry
  // button to re-run the case-details/accountant-payload fetch effect below
  // (it's a dep of that effect). No separate fetch path duplicated here.
  const [caseDetailsRefreshNonce, setCaseDetailsRefreshNonce] = useState(0);
  const [openSection, setOpenSection] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [nowline, setNowline] = useState<NowLineStats | null>(null);
  const [nowlineLoading, setNowlineLoading] = useState(true);

  // Phase 3 (D3 sign-off workflow) — which case's Sign-Off Readiness card
  // carries the current-location locus (`border-brand`). Set by the row's
  // SIGN OFF button (CaseTable → handleRowSignOff below); cleared when the
  // user selects a different case, or clicks elsewhere in the rail outside
  // the card (handleRailClick below). K3 D3 ruling: the row button OPENS
  // THE RAIL — it never POSTs. SignOffReadinessCard owns the entire
  // sign-off lifecycle (pre-flight → armed → failed → signed).
  const [signOffFocusCaseId, setSignOffFocusCaseId] = useState<string | null>(null);
  const signOffCardWrapperRef = useRef<HTMLDivElement | null>(null);

  // Phase 2 (D1 toolbar) — one search string, shared by the toolbar instance
  // rendered on whichever of Queue/My Cases is active; cleared on tab switch
  // (build spec §3) rather than kept per-tab.
  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    setSearchQuery('');
  }, [activeTab]);

  // `?filter=` is URL-driven (persists across tabs, back-button works) and
  // now covers the full all|ready|needs-review|stale set — extended from the
  // prior stat-card-only 'needs-review'/'ready' handling.
  const statusFilter: WorkdeskStatusFilterValue = parseStatusFilter(searchParams.get('filter'));
  const setStatusFilter = useCallback(
    (v: WorkdeskStatusFilterValue) => {
      const params = new URLSearchParams(searchParams);
      if (v === 'all') params.delete('filter');
      else params.set('filter', v);
      setSearchParams(params, { replace: false });
    },
    [searchParams, setSearchParams]
  );

  const loadNowline = useCallback(() => {
    setNowlineLoading(true);
    api
      .get('/api/v1/queue/nowline-stats?role=accountant', { showErrorToast: false })
      .then((data: any) => setNowline(data))
      .catch(() => setNowline(null))
      .finally(() => setNowlineLoading(false));
  }, []);

  useEffect(() => { loadNowline(); }, [loadNowline]);

  // Fetch case details when selected
  useEffect(() => {
    if (!selectedCaseId) {
      setCaseDetails(null);
      setCaseDetailsError(null);
      setCaseDetailsLoading(false);
      setAccountantPayload(null);
      return;
    }
    const controller = new AbortController();
    setCaseDetailsLoading(true);
    setCaseDetailsError(null);
    api
      .get(`/api/v1/dashboard/case-details/${selectedCaseId}`, {
        signal: controller.signal,
        showErrorToast: false,
      })
      .then((data: any) => {
        if (controller.signal.aborted) return;
        setCaseDetails(data || null);
      })
      .catch((err: any) => {
        if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return;
        setCaseDetailsError(err?.message || 'Failed to load case details');
        setCaseDetails(null);
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setCaseDetailsLoading(false);
      });

    // Fetch accountant payload
    setAccountantPayloadLoading(true);
    api
      .get(`/api/v1/dashboard/accountant-payload/${selectedCaseId}`, {
        signal: controller.signal,
        showErrorToast: false,
      })
      .then((data: any) => {
        if (controller.signal.aborted) return;
        setAccountantPayload(data || null);
      })
      .catch(() => {
        setAccountantPayload(null);
      })
      .finally(() => {
        setAccountantPayloadLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [selectedCaseId, caseDetailsRefreshNonce]);

  // Fetch stats — a named callback (Phase 5, D5) so the utility bar's
  // refresh button can re-trigger it alongside loadQueue/loadNowline.
  const [statsLoading, setStatsLoading] = useState(true);
  const loadStats = useCallback(() => {
    setStatsLoading(true);
    api
      .get('/api/v1/queue/stats?user_role=accountant', { showErrorToast: false })
      .then((data: any) => setStats(data))
      .catch(() => {})
      .finally(() => setStatsLoading(false));
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Lifted queue data — the parent owns the queue rows so the tab
  // counts (and, in Part B, the triage rail + work-queue) all read one source.
  // §1.2: server-paginated (page_size=50, the backend default — queue_api.py
  // :1385-1387, :1468-1469). `page` is a dep so a page change refetches.
  const loadQueue = useCallback(() => {
    setQueueLoading(true);
    api
      .get(`/api/v1/queue/accountant/available?page=${queuePage}&page_size=50&include_claimed=true&scope=${scope}`, { showErrorToast: false })
      .then((data: any) => {
        setQueueCases(data.cases || []);
        setQueueTotalCount(data.total_count || 0);
        setQueueTotalPages(data.total_pages || 1);
      })
      .catch(() => setQueueCases([]))
      .finally(() => setQueueLoading(false));
  }, [queuePage, scope]);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  // Claim-pool removed: one list (queueCases) split into active vs signed-off.
  const isSignedOff = (c: any) => c.signed_off === true || c.workflow_status === 'signed_off';
  const activeCases = useMemo(() => queueCases.filter((c: any) => !isSignedOff(c)), [queueCases]);
  const signedOffCases = useMemo(() => queueCases.filter((c: any) => isSignedOff(c)), [queueCases]);

  // Phase 3 (D3) — row SIGN OFF click: select the case (opening the rail if
  // it isn't already) and arm the sign-off-card locus. Deliberately NOT a
  // POST — no handleRequestSignOff, no signingOffCaseId/signOffError state
  // (K3 D3 ruling: those are cut entirely; SignOffReadinessCard owns the
  // POST). `setSelectedCaseId` + `setSignOffFocusCaseId` land in the same
  // React batch, so the scroll/locus effect below sees them equal on its
  // first run — the "different case" cleanup effect never fires spuriously.
  const handleRowSignOff = useCallback((id: string) => {
    setSelectedCaseId(id);
    setSignOffFocusCaseId(id);
  }, []);

  // Clear the locus once the rail moves to a different case (or closes).
  useEffect(() => {
    if (signOffFocusCaseId && signOffFocusCaseId !== selectedCaseId) {
      setSignOffFocusCaseId(null);
    }
  }, [selectedCaseId, signOffFocusCaseId]);

  // Scroll the Sign-Off Readiness card into view — INSTANT (`behavior:
  // 'auto'`), never 'smooth' (the motion law bans anything but ≤150ms
  // color-hover transitions). Skips the scroll when the card is already
  // fully in the viewport, per the D3 spec ("If the card is already in
  // view, skip the scroll, apply the locus only").
  useEffect(() => {
    if (!signOffFocusCaseId || signOffFocusCaseId !== selectedCaseId) return;
    const el = signOffCardWrapperRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const inView = rect.top >= 0 && rect.bottom <= window.innerHeight;
    if (!inView) {
      el.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
  }, [signOffFocusCaseId, selectedCaseId]);

  // Clicking anywhere in the rail OTHER than inside the sign-off card itself
  // clears the locus ("until the user selects a different case or clicks
  // elsewhere in the rail" — D3 spec). Clicks inside the card (arm/confirm/
  // retry) intentionally do not clear it.
  const handleRailClick = useCallback(
    (e: React.MouseEvent) => {
      if (!signOffFocusCaseId) return;
      const cardEl = signOffCardWrapperRef.current;
      if (cardEl && cardEl.contains(e.target as Node)) return;
      setSignOffFocusCaseId(null);
    },
    [signOffFocusCaseId]
  );

  // Tab counts are PAGE-scoped: both lists are derived client-side from the one
  // fetched queue page (the server total includes signed-off cases, so it can't
  // split into Cases vs Signed Off). The pager and the "Total" stat card use the
  // server `queueTotalCount` (all pages, claimed + signed-off included).
  const tabCount = (key: TabKey): number =>
    key === 'queue' ? activeCases.length : signedOffCases.length;

  const tabBlockerCount = (key: TabKey): number => {
    const list = key === 'queue' ? activeCases : signedOffCases;
    return list.filter((c: any) => (c.readiness_issues?.length || 0) > 0).length;
  };

  // Phase 2 (D1) "ON PAGE" rule — tab counts and blocker chips are both
  // page-scoped (derived from the fetched page's array). Once totalPages > 1
  // that needs disclosure AT the chip. 'signed' derives from the same page fetch.
  const tabTotalPages = (key: TabKey): number => (key === 'queue' || key === 'signed' ? queueTotalPages : 1);

  // Phase 2 (D1 toolbar) — local filter hook, replacing the old ad-hoc
  // `filteredQueueCases` memo (needs-review/ready only, queue-tab only).
  // Applied to the active (non-signed-off) list; `signedOffCases`
  // stays unfiltered — the toolbar doesn't render on the Signed Off tab.
  const filteredActiveCases = useWorkdeskCaseFilter(activeCases, searchQuery, statusFilter);
  const isFilterActive = searchQuery.trim() !== '' || statusFilter !== 'all';
  const clearAllFilters = useCallback(() => {
    setSearchQuery('');
    setStatusFilter('all');
  }, [setStatusFilter]);
  // Header "Clear filter ×" link (pre-existing, ?filter=-only — left as-is,
  // just extended to fire on the same statusFilter value the toolbar now
  // reads/writes, and to both tabs the toolbar covers).
  const clearFilter = () => setStatusFilter('all');
  // Non-visual only (aria-current) as of the K3 orange-census follow-up — no
  // stat card renders an orange locus bar any more (the active tab is the
  // sole location locus; see the four card buttons below).
  const availEngaged = activeTab === 'queue' && statusFilter === 'all';
  const needsEngaged = statusFilter === 'needs-review';
  const readyEngaged = statusFilter === 'ready';
  const portfolioEngaged = false;

  const tabButtonClass = (key: TabKey) =>
    `px-4 py-3 text-sm font-medium border-b-2 transition-colors min-h-[44px] ${
      activeTab === key
        ? 'border-brand text-brand'
        : 'border-transparent text-text-muted hover:text-text-secondary'
    }`;

  const tabMeta = (key: TabKey) => {
    const count = tabCount(key);
    const blockers = tabBlockerCount(key);
    // "N ON PAGE" once the tab spans more than one server page — page equals
    // global on a single page, so the cue disappears exactly when it stops
    // being necessary (D1 spec, "Tab blocker chips — the ON PAGE rule").
    const pageScoped = tabTotalPages(key) > 1;
    return (
      <>
        <span className="font-mono text-xs text-text-dim ml-1">{count}</span>
        {blockers > 0 && (
          <span
            className="font-mono text-[10px] px-1.5 py-0.5 rounded-[2px] ml-1"
            style={{ color: 'var(--color-blocker)', backgroundColor: 'var(--color-blocker-tint)', border: '1px solid var(--color-blocker-tint-bd)' }}
            title={`${blockers} case(s) with blockers${pageScoped ? ' on this page' : ''}`}
          >
            {pageScoped ? `${blockers} ON PAGE` : blockers}
          </span>
        )}
      </>
    );
  };

  // Phase 2 (D1) pager honesty-cluster inputs. `shownCount` stays null (the
  // pager's plain "start–end OF total" form) unless a search/status filter
  // is actually narrowing the CURRENT tab's page — never derived from the
  // other tab's filtered state.
  const queueRange = pageRange(queuePage, queueTotalCount);
  const queueShownCount = activeTab === 'queue' && isFilterActive ? filteredActiveCases.length : null;

  // Phase 4 (D4 #2) — collapse-surviving header chips for the two new rail
  // sections. Both MUST come from data the parent already has (the case-list
  // row / accountant-payload), never from the panels themselves — those only
  // mount once their section is opened. `selectedCaseSummary` looks in both
  // lifted lists (single queue list) since the rail can be opened from either tab,
  // or from QueueTriageRail (which reads off queueCases too).
  const selectedCaseSummary = useMemo(
    () =>
      queueCases.find((c: any) => c.id === selectedCaseId) ||
      null,
    [queueCases, selectedCaseId]
  );
  const selectedCaseReadinessCount = selectedCaseSummary?.readiness_issues?.length || 0;
  // Verified real field: accountant_dashboard_service.py `_build_data_quality`
  // -> `missing_doc_types` (data_quality slice of /dashboard/accountant-payload).
  // Not a fetch made for the chip's sake — accountantPayload is already loaded
  // for the Trust Signals DataQualityCard above.
  const missingDocsCount = accountantPayload?.data_quality?.missing_doc_types?.length || 0;

  // Phase 4 (D4 #3) — Industry Benchmarks empty-collapse. Mirrors
  // IndustryBenchmarkCard's own "no data" branch condition (`error || !metrics`)
  // so the rail's collapse decision agrees with what the full card would have
  // rendered, without duplicating its fetch. Loading stays on the full card
  // (so the spinner is visible); only a settled "confirmed empty" state collapses.
  const industryBenchmarkData = caseDetails?.industry_benchmark ?? (caseDetailsError ? { error: caseDetailsError } : null);
  const industryBenchmarkEmpty =
    !caseDetailsLoading &&
    (!industryBenchmarkData || !!industryBenchmarkData.error || industryBenchmarkData.available === false);

  // Phase 5 (D5 shell/nav) — the utility bar's Refresh button re-runs every
  // lifted data source at once; anyLoaderActive drives its spinner off the
  // SAME loading flags the tabs/rail already track (no new fetch tracking).
  const handleRefreshAll = useCallback(() => {
    loadQueue();
    loadNowline();
    loadStats();
  }, [loadQueue, loadNowline, loadStats]);
  const anyLoaderActive = queueLoading || nowlineLoading || statsLoading;

  return (
    <div className="min-h-screen lg:h-screen lg:overflow-hidden bg-bg-base p-6 lg:p-8" data-testid="internal-accountant-workdesk">
      <div className="max-w-[1600px] mx-auto lg:flex lg:flex-col lg:h-full lg:min-h-0">
        {/* Utility bar (Phase 5, D5) — first shrink-0 item, above the header. */}
        <WorkDeskUtilityBar
          roleLabel="Accountant WorkDesk"
          onRefresh={handleRefreshAll}
          isRefreshing={anyLoaderActive}
          roleTag="ACCOUNTANT"
        />

        {/* Header */}
        <div className="flex items-center justify-between mb-6 lg:shrink-0">
          <div>
            <h1
              className="text-2xl text-text-primary"
              style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
            >
              Accountant Work Desk
            </h1>
            <p className="text-text-muted mt-1">Review calculations, verify data, sign off on cases</p>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 lg:shrink-0">
          <button
            type="button"
            aria-current={availEngaged || undefined}
            onClick={() => {
              const params = new URLSearchParams(searchParams);
              params.set('tab', 'queue');
              params.delete('filter');
              setSearchParams(params, { replace: false });
            }}
            /* K3 pixel pass, orange-census ruling (a): the Available card's orange
             * locus is redundant with the orange Queue tab it duplicates (this card
             * is "engaged" by default any time you're on Queue with no filter, unlike
             * its siblings which require an explicit click). Dropped: uniform 1px
             * border like the sibling cards, no locus bar, hover = bg-hover only. */
            className="relative bg-bg-surface border border-border rounded-xl p-5 text-left w-full hover:bg-[var(--color-bg-hover)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand"
          >
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-bg-elevated">
                <FileText className="w-5 h-5 text-text-muted" />
              </div>
              <div>
                <p className="text-2xl font-bold text-text-primary">{queueTotalCount}</p>
                <p className="text-sm text-text-muted">Total</p>
              </div>
            </div>
          </button>

          <button
            type="button"
            aria-current={needsEngaged || undefined}
            onClick={() => {
              const params = new URLSearchParams(searchParams);
              params.set('tab', 'queue');
              params.set('filter', 'needs-review');
              setSearchParams(params, { replace: false });
            }}
            /* K3 orange-census (a), extended to all four cards per coordinator
             * follow-up: the active TAB is the sole location locus; a
             * filter-clickable stat card must never also carry orange (that's
             * a second locus for the same click). No locus bar, no orange
             * label, no orange hover — aria-current stays for a11y only. */
            className="relative bg-bg-surface border border-border rounded-xl p-5 text-left w-full hover:bg-[var(--color-bg-hover)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand"
          >
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-bg-elevated">
                <AlertTriangle className="w-5 h-5" style={{ color: (stats?.needs_attention || 0) > 0 ? 'var(--color-blocker)' : 'var(--color-text-muted)' }} />
              </div>
              <div>
                <p className="text-2xl font-bold text-text-primary">{stats?.needs_attention || 0}</p>
                <p className="text-sm text-text-muted">Need Review</p>
              </div>
            </div>
          </button>

          <button
            type="button"
            aria-current={readyEngaged || undefined}
            onClick={() => {
              const params = new URLSearchParams(searchParams);
              params.set('tab', 'queue');
              params.set('filter', 'ready');
              setSearchParams(params, { replace: false });
            }}
            className="relative bg-bg-surface border border-border rounded-xl p-5 text-left w-full hover:bg-[var(--color-bg-hover)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand"
          >
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-done-bg">
                <CheckCircle className="w-5 h-5 text-done" />
              </div>
              <div>
                <p className="text-2xl font-bold text-text-primary">{stats?.ready || 0}</p>
                <p className="text-sm text-text-muted">Ready</p>
              </div>
            </div>
          </button>

          <button
            type="button"
            aria-current={portfolioEngaged || undefined}
            onClick={() => {
              const params = new URLSearchParams(searchParams);
              params.set('tab', 'queue');
              params.delete('filter');
              setSearchParams(params, { replace: false });
            }}
            className="relative bg-bg-surface border border-border rounded-xl p-5 text-left w-full hover:bg-[var(--color-bg-hover)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand"
          >
            {/* Phase 3-refine (K3 §P3, 2026-08-22): icon dropped — the
                figure's own "$" (from formatCurrency's Intl currency
                format) already carries the unit; a redundant DollarSign
                icon beside it was a double-$ sighting. The other three
                stat cards keep their icons (their figures are unitless
                counts). */}
            <div>
              <p className="text-2xl font-medium text-text-primary" style={{ fontFamily: "'DM Mono', monospace" }}>
                <FinancialValue value={stats?.total_value} />
              </p>
              <p className="text-sm text-text-muted">Portfolio</p>
            </div>
          </button>
        </div>

        {/* Main Content: Tabs + Sidebar */}
        <div className="flex gap-6 lg:flex-1 lg:min-h-0">
          {/* Left: Tabs */}
          <div className="flex-1 min-w-0 lg:overflow-y-auto lg:min-h-0">
            {/* Tab Bar */}
            <div className="flex items-center gap-4 mb-6 border-b border-border" role="tablist">
              <button
                type="button"
                onClick={() => setActiveTab('queue')}
                role="tab"
                aria-selected={activeTab === 'queue'}
                className={tabButtonClass('queue')}
              >
                <div className="flex items-center gap-2">
                  <Unlock className="w-4 h-4" />
                  Cases
                  {tabMeta('queue')}
                </div>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('signed')}
                role="tab"
                aria-selected={activeTab === 'signed'}
                className={tabButtonClass('signed')}
              >
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Signed Off
                  {tabMeta('signed')}
                </div>
              </button>
              <div
                role="group"
                aria-label="Case scope"
                className="ml-auto mb-1 inline-flex items-center rounded-lg border border-border bg-bg-surface p-0.5"
              >
                {([
                  { id: 'queue', label: 'My Queue' },
                  { id: 'all', label: 'All Cases' },
                ] as const).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    aria-pressed={scope === opt.id}
                    onClick={() => {
                      if (scope === opt.id) return;
                      setScope(opt.id);
                      setQueuePage(1);
                    }}
                    className={`px-3 py-1 text-[13px] font-medium rounded-md transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand ${
                      scope === opt.id
                        ? 'bg-brand text-white'
                        : 'text-text-secondary hover:text-text-primary hover:bg-[var(--color-bg-hover)]'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {activeTab === 'queue' && statusFilter !== 'all' && (
                <button
                  type="button"
                  onClick={clearFilter}
                  className="text-[12px] text-brand hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand rounded px-1"
                >
                  Clear filter ×
                </button>
              )}
            </div>

            {scope === 'all' && (
              <p className="text-xs text-text-muted -mt-3 mb-4">
                Showing all company cases — every stage
              </p>
            )}

            <NowLineBanner data={nowline} loading={nowlineLoading} />

            {/* Tab Content */}
            <div className="bg-bg-surface border border-border rounded-xl overflow-hidden">
              {activeTab === 'queue' && (
                <>
                  <WorkdeskToolbar
                    searchQuery={searchQuery}
                    onSearchChange={setSearchQuery}
                    statusFilter={statusFilter}
                    onStatusFilterChange={setStatusFilter}
                  />
                  <QueueCasesList
                    cases={filteredActiveCases}
                    loading={queueLoading}
                    selectedCaseId={selectedCaseId}
                    onSelectCase={setSelectedCaseId}
                    onSignOff={handleRowSignOff}
                    isFiltered={isFilterActive}
                    onClearFilter={clearAllFilters}
                  />
                  <PaginationBar
                    page={queuePage}
                    totalPages={queueTotalPages}
                    rangeStart={queueRange.start}
                    rangeEnd={queueRange.end}
                    totalCount={queueTotalCount}
                    shownCount={queueShownCount}
                    pageRowCount={queueCases.length}
                    onPrev={() => setQueuePage((p) => Math.max(1, p - 1))}
                    onNext={() => setQueuePage((p) => Math.min(queueTotalPages, p + 1))}
                  />
                </>
              )}
              {activeTab === 'signed' && (
                <SignedOffList
                  cases={signedOffCases}
                  loading={queueLoading}
                  selectedCaseId={selectedCaseId}
                  onSelectCase={setSelectedCaseId}
                />
              )}
            </div>
          </div>

          {/* Right: Sidebar Widgets */}
          <div
            className="w-80 flex-shrink-0 hidden lg:block lg:min-h-0"
            onClickCapture={handleRailClick}
          >
            {selectedCaseId ? (
              <div className="flex flex-col lg:h-full lg:min-h-0 lg:overflow-hidden">
                {/* pinned header (A) */}
                <div className="shrink-0 flex items-center justify-between px-1 pb-3">
                  <span className="text-xs uppercase tracking-wide text-text-dim font-medium">Case Insights</span>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setHistoryOpen(true)}
                      aria-label="Open edit history"
                      title="Edit history"
                      className="p-1 rounded-[2px] text-text-dim hover:bg-bg-elevated hover:text-text-secondary transition-colors duration-150 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand"
                    >
                      <History className="w-4 h-4" strokeWidth={2.25} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedCaseId(null)}
                      aria-label="Close case insights panel"
                      className="p-1 rounded-[2px] text-text-dim hover:bg-bg-elevated hover:text-text-secondary transition-colors duration-150 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* scrolling body: B, C, E, F */}
                <div className="flex-1 min-h-0 lg:overflow-y-auto space-y-4 pr-1">
                  {/* Primary CTAs — grouped as one composed unit (uniform 8px rhythm) */}
                  <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => navigate(`/dashboard/accountant/workbench/${selectedCaseId}`)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-brand hover:bg-brand-hover rounded-[2px] shadow-sm font-medium text-sm transition-colors"
                    style={{ color: 'var(--color-on-brand)' }}
                  >
                    <span className="flex items-center gap-2">
                      <LayoutGrid className="w-4 h-4" />
                      Open P&L Workbench
                    </span>
                    <ExternalLink className="w-4 h-4 opacity-70" />
                  </button>

                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => navigate(`/dashboard/accountant/review/${selectedCaseId}`)}
                      className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-bg-surface hover:bg-bg-hover border border-border hover:border-brand/30 text-text-primary rounded-[2px] shadow-sm font-medium text-xs transition-colors"
                    >
                      <ClipboardCheck className="w-3.5 h-3.5 text-text-muted" />
                      Engine Review
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate(`/dashboard/accountant/export/${selectedCaseId}`)}
                      className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-bg-surface hover:bg-bg-hover border border-border hover:border-brand/30 text-text-primary rounded-[2px] shadow-sm font-medium text-xs transition-colors"
                    >
                      <Download className="w-3.5 h-3.5 text-text-muted" />
                      Export Center
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate(`/dashboard/accountant/anomalies/${selectedCaseId}`)}
                      className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-bg-surface hover:bg-bg-hover border border-border hover:border-brand/30 text-text-primary rounded-[2px] shadow-sm font-medium text-xs transition-colors"
                    >
                      <AlertTriangle className="w-3.5 h-3.5" style={{ color: 'var(--color-blocker)' }} />
                      Anomaly Review
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate(`/reports/${selectedCaseId}/analysis`)}
                      className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-bg-surface hover:bg-bg-hover border border-border hover:border-brand/30 text-text-primary rounded-[2px] shadow-sm font-medium text-xs transition-colors"
                    >
                      <TrendingUp className="w-3.5 h-3.5 text-text-muted" />
                      Financial Analysis
                    </button>
                  </div>
                  </div>

                  <HandoffBlock caseId={selectedCaseId} />

                  {/* Block ② — Trust Signals (compressed, always visible) */}
                  <div className="text-xs uppercase tracking-wide text-text-dim font-medium">Trust Signals</div>
                  <div className="space-y-4">
                    <DataQualityCard
                      data={accountantPayload?.data_quality ?? null}
                      isLoading={accountantPayloadLoading}
                    />
                    <div className="border-t border-border pt-4">
                      {industryBenchmarkEmpty ? (
                        // D4 #3 — reclaims ~130px vs the full ~170px card when
                        // there's confirmed nothing to show it.
                        <div className="h-10 flex items-center justify-between px-1">
                          <span className="text-xs text-text-muted">No benchmark data available</span>
                          <button
                            type="button"
                            onClick={() => setCaseDetailsRefreshNonce((n) => n + 1)}
                            className="text-[11px] text-brand hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand rounded px-1"
                          >
                            Retry
                          </button>
                        </div>
                      ) : (
                        <IndustryBenchmarkCard
                          reportId={selectedCaseId}
                          data={industryBenchmarkData}
                          isLoading={caseDetailsLoading}
                        />
                      )}
                    </div>
                  </div>

                  {/* Block ③ — Case Context accordion */}
                  <div className="text-xs uppercase tracking-wide text-text-dim font-medium">Case Context</div>
                  <div className="bg-bg-surface border border-border rounded-xl overflow-hidden">
                    <div className="divide-y divide-border-faint">
                      {/* War Room Summary */}
                      <div>
                        <button
                          type="button"
                          onClick={() => setOpenSection((s) => (s === 'war-room' ? null : 'war-room'))}
                          className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-bg-hover transition-colors"
                        >
                          <span className="text-xs uppercase tracking-wide text-text-dim font-medium">
                            War Room Summary
                          </span>
                          <ChevronDown
                            className={`w-4 h-4 text-text-muted transition-transform ${openSection === 'war-room' ? 'rotate-180' : ''}`}
                          />
                        </button>
                        {openSection === 'war-room' && (
                          <div className="px-4 pb-4">
                            <WarRoomSummaryCard
                              data={accountantPayload?.war_room_summary ?? null}
                              isLoading={accountantPayloadLoading}
                            />
                          </div>
                        )}
                      </div>

                      {/* Forensic Insights */}
                      <div>
                        <button
                          type="button"
                          onClick={() => setOpenSection((s) => (s === 'forensic' ? null : 'forensic'))}
                          className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-bg-hover transition-colors"
                        >
                          <span className="text-xs uppercase tracking-wide text-text-dim font-medium">
                            Forensic Insights
                          </span>
                          {(caseDetails?.forensic_insights?.red_flags?.length || 0) > 0 && (
                            <span className="ml-auto mr-2 font-mono text-[11px] text-text-dim">{caseDetails.forensic_insights.red_flags.length}</span>
                          )}
                          <ChevronDown
                            className={`w-4 h-4 text-text-muted transition-transform ${openSection === 'forensic' ? 'rotate-180' : ''}`}
                          />
                        </button>
                        {openSection === 'forensic' && (
                          <div className="px-4 pb-4">
                            <ForensicInsightsCard
                              reportId={selectedCaseId}
                              data={caseDetails?.forensic_insights ?? (caseDetailsError ? { error: caseDetailsError } : null)}
                              isLoading={caseDetailsLoading}
                            />
                          </div>
                        )}
                      </div>

                      {/* Insurance Questions */}
                      <div>
                        <button
                          type="button"
                          onClick={() => setOpenSection((s) => (s === 'insurance' ? null : 'insurance'))}
                          className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-bg-hover transition-colors"
                        >
                          <span className="text-xs uppercase tracking-wide text-text-dim font-medium">
                            Insurance Questions
                          </span>
                          <ChevronDown
                            className={`w-4 h-4 text-text-muted transition-transform ${openSection === 'insurance' ? 'rotate-180' : ''}`}
                          />
                        </button>
                        {openSection === 'insurance' && (
                          <div className="px-4 pb-4">
                            <InsuranceQuestionsCard
                              reportId={selectedCaseId}
                              data={caseDetails?.insurance_questions ?? (caseDetailsError ? { error: caseDetailsError } : null)}
                              isLoading={caseDetailsLoading}
                            />
                          </div>
                        )}
                      </div>

                      {/* Next Actions */}
                      <div>
                        <button
                          type="button"
                          onClick={() => setOpenSection((s) => (s === 'next-actions' ? null : 'next-actions'))}
                          className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-bg-hover transition-colors"
                        >
                          <span className="text-xs uppercase tracking-wide text-text-dim font-medium">
                            Next Actions
                          </span>
                          {(caseDetails?.next_actions?.length || 0) > 0 && (
                            <span className="ml-auto mr-2 font-mono text-[11px] text-text-dim">{caseDetails.next_actions.length}</span>
                          )}
                          <ChevronDown
                            className={`w-4 h-4 text-text-muted transition-transform ${openSection === 'next-actions' ? 'rotate-180' : ''}`}
                          />
                        </button>
                        {openSection === 'next-actions' && (
                          <div className="px-4 pb-4">
                            <NextActionsChecklist
                              reportId={selectedCaseId}
                              data={caseDetails?.next_actions ?? (caseDetailsError ? { error: caseDetailsError } : null)}
                              isLoading={caseDetailsLoading}
                            />
                          </div>
                        )}
                      </div>

                      {/* AI Activity */}
                      <div>
                        <button
                          type="button"
                          onClick={() => setOpenSection((s) => (s === 'ai-activity' ? null : 'ai-activity'))}
                          className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-bg-hover transition-colors"
                        >
                          <span className="text-xs uppercase tracking-wide text-text-dim font-medium">
                            AI Activity
                          </span>
                          <ChevronDown
                            className={`w-4 h-4 text-text-muted transition-transform ${openSection === 'ai-activity' ? 'rotate-180' : ''}`}
                          />
                        </button>
                        {openSection === 'ai-activity' && (
                          <div className="px-4 pb-4">
                            <AIActivityCard
                              reportId={selectedCaseId}
                              data={caseDetails?.ai_activity ?? (caseDetailsError ? { error: caseDetailsError } : null)}
                              isLoading={caseDetailsLoading}
                            />
                          </div>
                        )}
                      </div>

                      {/* Phase 4 (D4 #2) — Extracted Documents. Reuses
                          ExtractedDocumentsPanel unmodified (self-fetches
                          GET /api/v1/supporting-documents/case/{id}), mount-on-open.
                          Header chip: missing-doc-types count already sitting in
                          accountantPayload (NOT a panel fetch) — omitted at 0. */}
                      <div>
                        <button
                          type="button"
                          onClick={() => setOpenSection((s) => (s === 'extracted-documents' ? null : 'extracted-documents'))}
                          className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-bg-hover transition-colors"
                        >
                          <span className="text-xs uppercase tracking-wide text-text-dim font-medium">
                            Extracted Documents
                          </span>
                          {missingDocsCount > 0 && (
                            <span
                              className="ml-auto mr-2 font-mono text-[10px] px-1.5 py-0.5 rounded-[2px]"
                              style={{ color: 'var(--color-blocker)', backgroundColor: 'var(--color-blocker-tint)', border: '1px solid var(--color-blocker-tint-bd)' }}
                              title={`${missingDocsCount} missing document type(s)`}
                            >
                              {missingDocsCount} MISSING
                            </span>
                          )}
                          <ChevronDown
                            className={`w-4 h-4 text-text-muted transition-transform ${openSection === 'extracted-documents' ? 'rotate-180' : ''}`}
                          />
                        </button>
                        {openSection === 'extracted-documents' && (
                          <div className="px-4 pb-4">
                            {/* D4 old-token containment: boundary wrapper holds the
                                WorkDesk's squared grammar at the seam; the scoped
                                --color-verified remap keeps any of the panel's
                                internal "done" green hueless WITHIN the WorkDesk
                                only (StaffDashboard, which also renders this panel,
                                is untouched — the remap lives on this wrapper, not
                                on the shared component). */}
                            <div
                              className="rounded-[2px] border border-border bg-[var(--color-bg-surface)] overflow-hidden p-3 [--color-verified:var(--color-verified)]"
                            >
                              <ExtractedDocumentsPanel caseId={selectedCaseId} compact />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Phase 4 (D4 #2) — Readiness Detail. Reuses
                          CaseReadinessPanel unmodified (self-fetches
                          GET /api/v1/queue/case/{id}/readiness), mount-on-open.
                          Header chip mirrors the pinned Sign-Off Readiness card's
                          "N BLOCKERS" language, sourced from the case-list row's
                          readiness_issues (already in queueCases — no
                          fetch for the chip). */}
                      <div>
                        <button
                          type="button"
                          onClick={() => setOpenSection((s) => (s === 'readiness-detail' ? null : 'readiness-detail'))}
                          className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-bg-hover transition-colors"
                        >
                          <span className="text-xs uppercase tracking-wide text-text-dim font-medium">
                            Readiness Detail
                          </span>
                          {selectedCaseReadinessCount > 0 && (
                            <span
                              className="ml-auto mr-2 font-mono text-[10px] px-1.5 py-0.5 rounded-[2px]"
                              style={{ color: 'var(--color-blocker)', backgroundColor: 'var(--color-blocker-tint)', border: '1px solid var(--color-blocker-tint-bd)' }}
                              title={`${selectedCaseReadinessCount} blocker(s)`}
                            >
                              {selectedCaseReadinessCount} BLOCKER{selectedCaseReadinessCount !== 1 ? 'S' : ''}
                            </span>
                          )}
                          <ChevronDown
                            className={`w-4 h-4 text-text-muted transition-transform ${openSection === 'readiness-detail' ? 'rotate-180' : ''}`}
                          />
                        </button>
                        {openSection === 'readiness-detail' && (
                          <div className="px-4 pb-4">
                            <div
                              className="rounded-[2px] border border-border bg-[var(--color-bg-surface)] overflow-hidden p-3 [--color-verified:var(--color-verified)]"
                            >
                              <CaseReadinessPanel caseId={selectedCaseId} compact showRefresh />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* pinned footer: the Sign-Off Readiness card (D), moved here verbatim.
                    Phase 3 (D3) wraps it in a locus boundary — `signOffCardWrapperRef` is
                    the scrollIntoView + border-brand target the row's SIGN OFF button
                    arms via `handleRowSignOff`. `padding: 2px` keeps the card's own
                    rounded-xl corners from being clipped by the wrapper's squared
                    rounded-[2px] (design law: new elements are squared). */}
                <div className="shrink-0 pt-4 border-t border-border">
                  <div
                    ref={signOffCardWrapperRef}
                    className="rounded-[2px]"
                    style={
                      signOffFocusCaseId && signOffFocusCaseId === selectedCaseId
                        ? { border: '1px solid var(--color-brand)', padding: 2 }
                        : undefined
                    }
                  >
                    <SignOffReadinessCard
                      signOffReady={accountantPayload?.sign_off_ready ?? null}
                      signOffBlockers={accountantPayload?.sign_off_blockers ?? null}
                      isLoading={accountantPayloadLoading}
                      caseId={selectedCaseId ?? undefined}
                      onSignedOff={() => {
                        // The card shows its own "Signed Off" confirmation (local doneAt), so we
                        // don't refetch the payload here (avoids flashing the rail). Refresh the
                        // top stats, the nowline, AND the queue list (Phase 3 D3
                        // addition) so the row actually drops out of "canSignOff" / moves to the
                        // Signed Off tab rather than sitting stale until the next manual refresh.
                        // (Phase 5, D5: routed through the named loadStats callback — was an
                        // inline duplicate of the same GET.)
                        loadStats();
                        loadNowline();
                        loadQueue();
                      }}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <QueueTriageRail cases={queueCases} onSelectCase={setSelectedCaseId} />
            )}
          </div>
        </div>

        {historyOpen && selectedCaseId && (
          <HistoryModal
            key={selectedCaseId}
            reportId={selectedCaseId}
            onClose={() => setHistoryOpen(false)}
          />
        )}
      </div>

      {/* Phase 5 (D5) — self-contained pipeline toasts (own WebSocket, no
          layout dependency); mounted once at the WorkDesk root per the
          chromeless-family fallback. */}
      <AgentNotification position="bottom-right" maxNotifications={4} autoDismissMs={8000} />
    </div>
  );
}
